import React, { useState, useEffect } from "react";
import { t } from "@lingui/macro";
import { i18n, OusPickerField } from "idc-ui-react";

// Fluent UI

import {
  Stack,
  PrimaryButton,
  MessageBar,
  MessageBarType,
  Dropdown,
  Dialog,
  DialogType,
  Spinner
} from "@fluentui/react";

// Our Modules

import { store } from "../../../../stores/Main";

const dropdownStyles = {
  dropdown: { width: 400 }
};

const SystemReportDropdown = ({ store, setSelectedSystem, isSystemOwner }) => {
  const { systems, isFetchingSystems } = store.state;

  const options = systems
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(system => {
      return { key: system.id, text: system.name };
    });

  const [canSelectSystems, setCanSelectSystems] = useState(isFetchingSystems);

  useEffect(() => {
    setCanSelectSystems(isFetchingSystems);
  }, [isFetchingSystems]);

  return (
    <>
      {!isSystemOwner && !canSelectSystems ? (
        <MessageBar messageBarType={MessageBarType.warning}>
          {i18n._(
            t`You are not a system owner for any IT-systems, so you are not authorized to download a system report.`
          )}
        </MessageBar>
      ) : null}

      <Dropdown
        disabled={!isSystemOwner || canSelectSystems}
        placeholder={i18n._(t`Select an option`)}
        label={i18n._(t`Select a System`)}
        onChange={(event, item) => setSelectedSystem(item.key)}
        options={options}
        styles={dropdownStyles}
      />
    </>
  );
};

const ManagerReportDropdown = ({ store, setSelectedOu, selectedOu }) => {
  const { ous, isFetchingOrganisationalUnits } = store.state;

  const [canSelectOrganisations, setCanSelectOrganisations] = useState(
    isFetchingOrganisationalUnits
  );
  const [options, setOptions] = useState();
  const [isManager, setIsManager] = useState(false);

  useEffect(() => {
    if (Object.keys(ous).length === 0) return;
    const checkReadonly = item => {
      if (!item.options.includes("READ_ONLY")) {
        setIsManager(true);
      }
      if (item.children && item.children.length) {
        item.children.forEach(child => {
          checkReadonly(child);
        });
      }
    };

    checkReadonly(ous);
    setOptions(ous);
  }, [ous]);

  useEffect(() => {
    setCanSelectOrganisations(isFetchingOrganisationalUnits);
  }, [isFetchingOrganisationalUnits]);

  if (!options) {
    return (
      <>
        <Dropdown
          disabled={true}
          placeholder={i18n._(t`Select an option`)}
          label={i18n._(t`Select an Organisational Unit`)}
          styles={dropdownStyles}
        />
      </>
    );
  }

  return (
    <Stack styles={{ root: { width: "100%" } }}>
      {!isManager && !canSelectOrganisations ? (
        <MessageBar messageBarType={MessageBarType.warning}>
          {i18n._(
            t`You are not a manager for any employees, so you are not authorized to download a manager report.`
          )}
        </MessageBar>
      ) : null}

      <OusPickerField
        disabled={canSelectOrganisations}
        placeholder={i18n._(t`Select an option`)}
        label={i18n._(t`Select an Organisational Unit`)}
        onChange={(e, item) => setSelectedOu(item.id)}
        options={options}
        styles={dropdownStyles}
        value={selectedOu}
        setValue={setSelectedOu}
        horizontal
      />
    </Stack>
  );
};

export function ReportDownloadView({ item }) {
  const { isFetchingReport, systems } = store.state;

  const [selectedSystem, setSelectedSystem] = useState(undefined);

  const [selectedOu, setSelectedOu] = useState(undefined);

  const [isDialogHidden, setIsDialogHidden] = useState(true);

  const [isSystemOwner, setIsSystemOwner] = useState(false);

  const [disableButton, setDisableButton] = useState(true);

  // spinner handler

  useEffect(() => {
    setIsDialogHidden(!isFetchingReport);
  }, [isFetchingReport]);

  useEffect(() => {
    setSelectedOu(undefined);

    setSelectedSystem(undefined);
  }, [item]);

  useEffect(() => {
    if (!item) return;

    setIsSystemOwner(
      systems && systems.length && item.reportType === "SYSTEMS"
    );
  }, [item, systems]);

  useEffect(() => {
    if (!item) return;

    setDisableButton(
      (item.reportType === "MANAGER" && selectedOu === undefined) ||
        (item.reportType === "SYSTEMS" && selectedSystem === undefined)
    );
  }, [item, selectedOu, selectedSystem]);

  const getReport = () => {
    const id =
      item.reportType === "SYSTEMS"
        ? selectedSystem
        : item.reportType === "MANAGER"
        ? selectedOu
        : "";

    const endpoint = item.endpoint;
    if (!endpoint) return;
    store.getReport(endpoint, item.fileType, id);
  };

  return (
    <Stack>
      <Stack
        horizontal
        tokens={{ childrenGap: 16 }}
        styles={{ root: { margin: "16px 0 16px 8px" } }}
      >
        <Stack styles={{ root: { width: "400px" } }}>
          {item.reportType === "SYSTEMS" && (
            <SystemReportDropdown
              isSystemOwner={isSystemOwner}
              store={store}
              selectedSystem={selectedSystem}
              setSelectedSystem={setSelectedSystem}
            />
          )}

          {item.reportType === "MANAGER" && (
            <ManagerReportDropdown
              store={store}
              selectedOu={selectedOu}
              setSelectedOu={setSelectedOu}
            />
          )}
        </Stack>

        <Stack verticalAlign="end">
          <PrimaryButton
            disabled={disableButton}
            onClick={getReport}
            text={i18n._(t`Get Report`)}
          />
        </Stack>
      </Stack>

      <Dialog
        hidden={isDialogHidden}
        dialogContentProps={{
          type: DialogType.normal,
          showCloseButton: false
        }}
      >
        <Spinner
          label={
            <Stack>
              {i18n._(t`Downloading report...`)}

              <Stack>{i18n._(t`This may take a few minutes...`)}</Stack>
            </Stack>
          }
        />
      </Dialog>
    </Stack>
  );
}
